import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Usuario } from '../../types';
import './KanbanFilters.css';

export interface KanbanFiltersValue {
  nome: string;
  cpf: string;
  telefone: string;
  atendenteId: string;
  /** Mostrar só cartões com handoff do Funil IA (qualquer coluna de vendas). */
  somenteHandoffFunilIa?: boolean;
  /** Mostrar só cartões marcados como reativação IA. */
  somenteReativacaoIa?: boolean;
}

interface KanbanFiltersProps {
  value: KanbanFiltersValue;
  users: Usuario[];
  showAtendenteFilter: boolean;
  onChange: (next: KanbanFiltersValue) => void;
  onCreateLead?: () => void;
  isCreateLeadExpanded?: boolean;
}

export const KanbanFilters: React.FC<KanbanFiltersProps> = ({
  value,
  users,
  showAtendenteFilter,
  onChange,
  onCreateLead,
  isCreateLeadExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const setField = (field: keyof KanbanFiltersValue, fieldValue: string) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const clearFilters = () => {
    onChange({
      nome: '',
      cpf: '',
      telefone: '',
      atendenteId: '',
      somenteHandoffFunilIa: false,
      somenteReativacaoIa: false,
    });
  };

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      value.nome.trim() ||
      value.cpf.trim() ||
      value.telefone.trim() ||
      value.atendenteId ||
      value.somenteHandoffFunilIa ||
      value.somenteReativacaoIa
    );
  }, [value]);

  return (
    <section className="kanban-filters">
      <div className="kanban-filters-header">
        <div className="kanban-inline-search">
          <button
            type="button"
            className={`kanban-inline-plus ${isCreateLeadExpanded ? 'active' : ''}`}
            onClick={onCreateLead}
            title="Criar Lead"
          >
            +
          </button>
          <input
            id="filter-nome"
            type="text"
            value={value.nome}
            onChange={(e) => setField('nome', e.target.value)}
            placeholder="Pesquisar"
          />
        </div>
        <div className="kanban-filters-actions">
          <button
            type="button"
            className={`kanban-toggle-button icon-toggle ${hasActiveFilters && !isExpanded ? 'has-active-filters' : ''}`}
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? 'Minimizar filtros' : 'Expandir filtros'}
            title={isExpanded ? 'Minimizar filtros' : 'Expandir filtros'}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {hasActiveFilters && !isExpanded && <span className="active-filter-badge" />}
          </button>
          <button
            type="button"
            className="clear-filters-button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Limpar
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="kanban-filters-body">
          <div className="kanban-filter-field">
            <label htmlFor="filter-cpf">CPF</label>
            <input
              id="filter-cpf"
              type="text"
              value={value.cpf}
              onChange={(e) => setField('cpf', e.target.value)}
              placeholder="Filtrar por CPF"
            />
          </div>

          <div className="kanban-filter-field">
            <label htmlFor="filter-telefone">Telefone</label>
            <input
              id="filter-telefone"
              type="text"
              value={value.telefone}
              onChange={(e) => setField('telefone', e.target.value)}
              placeholder="Filtrar por telefone"
            />
          </div>

          {showAtendenteFilter && (
            <div className="kanban-filter-field">
              <label htmlFor="filter-atendente">Atendente</label>
              <select
                id="filter-atendente"
                value={value.atendenteId}
                onChange={(e) => setField('atendenteId', e.target.value)}
              >
                <option value="">Todos os atendentes</option>
                {users.map((user) => (
                  <option key={user.usuarioId} value={user.usuarioId}>
                    {user.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="kanban-filter-field kanban-filter-field--checkbox">
            <div className="kanban-filter-checkbox-block">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(value.somenteHandoffFunilIa)}
                  onChange={(e) => onChange({ ...value, somenteHandoffFunilIa: e.target.checked })}
                />
                Só handoff Funil IA
              </label>
              <span className="kanban-filter-hint">
                Cartões que passaram pelo funil IA e entraram no Kanban humano.
              </span>
            </div>
          </div>
          <div className="kanban-filter-field kanban-filter-field--checkbox">
            <div className="kanban-filter-checkbox-block">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(value.somenteReativacaoIa)}
                  onChange={(e) => onChange({ ...value, somenteReativacaoIa: e.target.checked })}
                />
                Só reativação IA
              </label>
              <span className="kanban-filter-hint">
                Cartões com intenção de reativação detectada no inbound.
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
